import type { PromptQueue } from 'OpalChat/promptQueue';

/** The federated hook's type, queried without importing it as a value (keeps it lazy). */
type UsePromptQueue = Awaited<typeof import('OpalChat/promptQueue')>['usePromptQueue'];

let promptQueuePromise: Promise<UsePromptQueue> | undefined;

/**
 * Lazily loads the federated `OpalChat/promptQueue` singleton, mirroring the
 * reactapp host loader. `usePromptQueue` is imperative (not a React hook) — it
 * returns `{ enqueuePrompt }` bound to a module-level store, so enqueuing works
 * from any callback and survives navigation. The remote chunk is fetched on the
 * first `getPromptQueue()` call (not at module load, so importing this file never
 * pulls the chat remote into the app-shell bundle), then memoized. If the remote
 * fails to load, falls back to a queue whose `enqueuePrompt` rejects, so the
 * caller's `.catch` can surface the failure to the user instead of silently
 * dropping the prompt — and the memo is cleared so the *next* call retries the
 * import rather than being stuck on the fallback forever (a transient blip on the
 * first load must not permanently disable Ask Opal).
 */
export const getPromptQueue = (): Promise<UsePromptQueue> => {
  promptQueuePromise ||= import('OpalChat/promptQueue')
    .then(m => m.usePromptQueue)
    .catch((error: unknown) => {
      console.error('Failed to load OpalChat/promptQueue:', error);
      // Drop the memo so a later call re-imports; the promise already returned to
      // THIS caller still resolves to the rejecting fallback below.
      promptQueuePromise = undefined;
      const failed: PromptQueue = {
        enqueuePrompt: () => Promise.reject(new Error('OpalChat/promptQueue failed to load')),
      };
      return () => failed;
    });
  return promptQueuePromise;
};
